import { CONNECTORS_V1, STOP_WORDS_V1 } from '../pack/lexicons';

import { segmentWordTokens } from './word-tokens';

/**
 * `operandEvidence(span)`: whether anything worth acting on remains in a span.
 *
 * A span has evidence when at least one content token survives removing connectors and
 * `STOP_WORDS_V1`. This single predicate is what separates a real Chat request from a
 * bare cue: `summarize the page` has evidence, `summarize this` does not.
 *
 * Filtering is case-folded and applies **only** to this derived span. `qMatch` and
 * `wordTokens` keep every stop word, so token counts and rule positions never move.
 *
 * The design's definition also counts "one complete protected atom" as evidence on its
 * own. That clause is deliberately **not** implemented: a protected atom can be empty
 * (`""`, `"   "`, `"???"`), so honouring it made `explain ""` and `open ""` look like
 * real requests. Counting the words *inside* a quoted range or URL instead is both
 * simpler and stricter — `explain "launch plan"` and `explain https://example.com/plan`
 * still have evidence, and an empty quote has none.
 */

const FILTERED_WORDS: ReadonlySet<string> = new Set([...STOP_WORDS_V1, ...CONNECTORS_V1]);

/** Returns the content tokens of a span: word-like, and not a filtered word. */
export const getContentTokens = (span: string): readonly string[] =>
	segmentWordTokens(span).tokens.filter((token) => !FILTERED_WORDS.has(token.toLowerCase()));

export const hasOperandEvidence = (span: string): boolean => getContentTokens(span).length > 0;

/**
 * Returns whether a span contains no letters but does contain digits.
 *
 * `C-WH-QUESTION` requires a non-numeric remainder: `what 12 34` is a Search query
 * about numbers, not a question the Chat destination can answer.
 */
export const isNumericOnly = (span: string): boolean => /\d/u.test(span) && !/\p{L}/u.test(span);
